import { ArrowUpRight } from "lucide-react";
import { FadeUp, Eyebrow, MaskedLines } from "./Reveal";

const PROJECTS = [
  {
    id: "crf",
    tag: "Website — Design & Build",
    title: "CRF Gardening & Maintenance",
    desc: "A dark, modern home for a Canberra gardening crew — services laid out clean, before-and-after proof of work, and a quote funnel built to turn visitors into calls.",
    url: "https://crfgardeningandmaintenance.com",
    img: "/work/work-crf.jpg",
    device: "laptop",
  },
  {
    id: "ap",
    tag: "Website — Design & Build",
    title: "AP Pumping — Concrete Pumping",
    desc: "A bold, dark build for a concrete pumping crew — live rates, project types and tap-to-call actions front and centre, ready for jobs at any hour.",
    url: "https://pump-blow-go.preview.emergentagent.com",
    img: "/work/work-pump.jpg",
    device: "phone",
  },
];

function Laptop({ img, alt }) {
  return (
    <div data-testid="work-shot-crf">
      <div className="laptop-screen">
        <img
          src={img}
          alt={alt}
          className="site-scroll w-full h-auto"
          style={{ "--screen-h": "400px" }}
          loading="lazy"
        />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_8%,rgba(212,175,55,0.14),transparent_55%)]" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
      </div>
      <div className="laptop-base" />
    </div>
  );
}

function Phone({ img, alt }) {
  return (
    <div
      data-testid="work-shot-ap"
      className="relative mx-auto w-[min(300px,100%)]"
      style={{ aspectRatio: "848 / 1264" }}
    >
      <img
        src="/work/phone-frame.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full"
      />
      <div
        className="phone-real-screen overflow-hidden"
        style={{
          backgroundImage: `url(${img})`,
          top: "12.3%",
          right: "22%",
          bottom: "12.3%",
          left: "21.8%",
          borderRadius: "1.6rem",
        }}
        role="img"
        aria-label={alt}
      >
        <span className="phone-sheen" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_8%,rgba(212,175,55,0.10),transparent_55%)]" />
      </div>
    </div>
  );
}

function Project({ p, flip }) {
  return (
    <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
      <div className={flip ? "lg:order-2 lg:col-span-5" : "lg:col-span-5"}>
        <FadeUp>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E5C158]/90">
            {p.tag}
          </p>
          <h3 className="mt-4 font-display text-2xl font-light tracking-tight sm:text-3xl">
            {p.title}
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-neutral-400">{p.desc}</p>
          <a
            data-testid={`work-link-${p.id}`}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#E5C158] transition-colors duration-300 hover:text-white"
          >
            Visit the site
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </FadeUp>
      </div>
      <div className={flip ? "lg:order-1 lg:col-span-7" : "lg:col-span-7"}>
        <FadeUp delay={0.12}>
          {p.device === "laptop" ? (
            <Laptop img={p.img} alt={`${p.title} website`} />
          ) : (
            <Phone img={p.img} alt={`${p.title} website`} />
          )}
        </FadeUp>
      </div>
    </div>
  );
}

export default function WorkShowcase() {
  return (
    <section id="work" data-testid="work-section" className="relative py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <Eyebrow className="mb-6">02 — Work</Eyebrow>
        <MaskedLines
          inView
          as="h2"
          className="font-display text-2xl font-light leading-tight tracking-tight sm:text-4xl"
          lines={[
            { content: "Recent builds." },
            { content: <span className="italic text-[#E5C158]">Shipped & live.</span> },
          ]}
        />
        <div className="mt-14 space-y-20 sm:space-y-28">
          {PROJECTS.map((p, i) => (
            <Project key={p.id} p={p} flip={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
}